import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { comingSoon } from '../../content/site';
import { isPending } from '../../lib/pending';

/**
 * Aviso de «funcionalidad en desarrollo» para los CTA sin destino.
 *
 * Un solo aviso vivo a la vez, en un provider en la raíz: si cada botón tuviera
 * el suyo, dos clics seguidos apilarían tarjetas y el `aria-live` cantaría las
 * dos a la vez.
 */

interface Notice {
  /** Cambia en cada clic para remontar la tarjeta y repetir la animación. */
  readonly id: number;
  readonly action: string;
  readonly quip: string;
}

type Notify = (action: string) => void;

const NotifyContext = createContext<Notify>(() => {});

/** Milisegundos que el aviso permanece en pantalla. */
const AUTO_DISMISS_MS = 6500;

/**
 * Devuelve los props que convierten un enlace pendiente en disparador del
 * aviso. Si el destino ya existe devuelve un objeto vacío, así que el enlace
 * navega con normalidad y el call site no necesita condicionales.
 */
export function usePendingLink() {
  const notify = useContext(NotifyContext);

  return useCallback(
    (
      href: string,
      action: string,
    ): Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> => {
      if (!isPending(href)) return {};

      return {
        onClick: (event) => {
          /*
           * Sólo se intercepta el clic primario y sin modificadores: ctrl/cmd o
           * el botón central abren en pestaña nueva, y ahí preferimos dejar que
           * el navegador haga lo suyo antes que tragarnos la interacción.
           */
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
          }

          event.preventDefault();
          notify(action);
        },
      };
    },
    [notify],
  );
}

export function PendingNoticeProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [notice, setNotice] = useState<Notice | null>(null);
  /** Clics acumulados en la sesión — alimenta el guiño a la insistencia. */
  const clicks = useRef(0);

  const notify = useCallback<Notify>((action) => {
    const attempt = clicks.current;
    clicks.current += 1;

    const quip =
      comingSoon.quips[attempt] ??
      comingSoon.insistTemplate.replace('{veces}', String(clicks.current));

    setNotice({ id: clicks.current, action, quip });
  }, []);

  // Auto-cierre. El timer se reinicia con cada aviso nuevo porque `notice`
  // cambia de identidad en cada clic.
  useEffect(() => {
    if (!notice) return;

    const timer = window.setTimeout(() => setNotice(null), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!notice) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNotice(null);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [notice]);

  // `notify` es estable, pero el provider recibe un objeto nuevo en cada render
  // del estado si no se memoiza.
  const value = useMemo(() => notify, [notify]);

  return (
    <NotifyContext.Provider value={value}>
      {children}

      {/*
       * El contenedor vive siempre montado y con aria-live: si apareciera junto
       * al texto, algunos lectores de pantalla no anunciarían nada, porque la
       * región tiene que existir antes de que su contenido cambie.
       */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-4 bottom-4 z-110 mx-auto max-w-[420px] sm:inset-x-auto sm:right-6 sm:bottom-6 sm:mx-0"
      >
        {notice && (
          <div
            key={notice.id}
            // Borde claro y no `border-line-dark`: el aviso también aparece
            // sobre el footer negro, donde un borde oscuro lo haría invisible.
            className="animate-toast-in pointer-events-auto rounded-card border border-white/18 bg-ink-deep p-5 shadow-card-white"
          >
            <div className="flex items-start justify-between gap-4">
              <p className="font-display text-[10.5px] font-semibold tracking-[0.22em] text-brand-gold">
                {comingSoon.badge}
              </p>

              <button
                type="button"
                onClick={() => setNotice(null)}
                aria-label={comingSoon.dismiss}
                className="-mt-1.5 -mr-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-btn text-ondark-soft transition-colors hover:bg-white/10 hover:text-white"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  aria-hidden="true"
                  focusable="false"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <p className="mt-2.5 text-[14.5px] leading-[1.6] text-ondark-strong">
              {comingSoon.bodyTemplate.replace('{accion}', notice.action)}
            </p>
            <p className="mt-1.5 text-[13.5px] leading-[1.6] text-ondark-soft">
              {notice.quip}
            </p>
          </div>
        )}
      </div>
    </NotifyContext.Provider>
  );
}
